// React Query hooks for the contract Reports tab: the summary, delivery
// performance, KPI compliance and financial performance read models, the
// manager-commentary write and the period-level service-credit writes. Mirrors useFinance.ts conventions —
// positional-array query keys, the shared `trackingRetry` policy, and
// invalidate-on-mutation-success.
//
// The report models are composed entirely server-side, so these hooks do no
// shaping: `data` is the wire model verbatim.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { reportApi } from "../services/api";
import { trackingRetry } from "./trackingRetry";
import type {
  DeliveryRecordType,
  DeliveryReportModel,
  FinancialReportModel,
  KpiReportModel,
  ReportCommentary,
  ReportCommentaryPutInput,
  ReportRange,
  ServiceCredit,
  ServiceCreditDeleteInput,
  ServiceCreditPutInput,
  SummaryReportModel,
} from "../types/report";

/** `["report", contractId]` is a prefix match, so invalidating it refreshes
 *  the summary and every focused view, window and filter cached under the
 *  same contract. A commentary save changes the summary's commentary block
 *  and its freshness line, so the broad prefix is correct: the screen must
 *  converge on what the server actually stored. */
function invalidateReport(
  queryClient: ReturnType<typeof useQueryClient>,
  contractId: string,
): void {
  queryClient.invalidateQueries({ queryKey: ["report", contractId] });
}

// ─── Queries ─────────────────────────────────────────────────────────────

export function useReportSummary(contractId: string) {
  return useQuery<SummaryReportModel>({
    queryKey: ["report", contractId, "summary"],
    queryFn: () => reportApi.getSummary(contractId),
    enabled: !!contractId,
    retry: trackingRetry,
  });
}

/**
 * The delivery performance model for one window and record type.
 *
 * The record type is part of the key, not a client-side filter: the server
 * applies it before any derivation runs, so the metrics, the monthly buckets
 * and the late-item table all describe the same records. Filtering an
 * all-records payload here would put three disagreeing answers on one screen.
 */
export function useReportDelivery(
  contractId: string,
  range: ReportRange,
  recordType: DeliveryRecordType,
) {
  return useQuery<DeliveryReportModel>({
    queryKey: ["report", contractId, "delivery", range, recordType],
    queryFn: () => reportApi.getDelivery(contractId, range, recordType),
    enabled: !!contractId,
    retry: trackingRetry,
  });
}

/**
 * The KPI compliance model for one window.
 *
 * Service credits arrive inside this model, so the KPIs tab reads it at the
 * `all` range to list every recorded credit rather than a second endpoint
 * answering the same rows.
 */
export function useReportKpi(contractId: string, range: ReportRange) {
  return useQuery<KpiReportModel>({
    queryKey: ["report", contractId, "kpi", range],
    queryFn: () => reportApi.getKpi(contractId, range),
    enabled: !!contractId,
    retry: trackingRetry,
  });
}

/**
 * The financial performance model for one window.
 *
 * Only the monthly rows and the average approval time follow the window; the
 * value totals and the awaiting-approval queue are contract-to-date, so they
 * agree with the cumulative position rendered beside them. That split lives
 * in the composer, which is why the range belongs in the key rather than in a
 * client-side filter over a wider payload.
 */
export function useReportFinancial(contractId: string, range: ReportRange) {
  return useQuery<FinancialReportModel>({
    queryKey: ["report", contractId, "financial", range],
    queryFn: () => reportApi.getFinancial(contractId, range),
    enabled: !!contractId,
    retry: trackingRetry,
  });
}

/**
 * Force-read the contract's summary and hand back just its commentary block.
 *
 * Used by the conflict path in the commentary editor: after a 409 the author
 * needs to see what actually landed, and needs the version it landed at
 * before they can choose to write over it. There is no commentary GET — the
 * summary is the only read — so this goes through the same query key the
 * screen uses, which also refreshes the rest of the summary as a side effect.
 */
export function useFetchLatestCommentary() {
  const queryClient = useQueryClient();
  return (contractId: string): Promise<SummaryReportModel["commentary"]> =>
    queryClient
      .fetchQuery<SummaryReportModel>({
        queryKey: ["report", contractId, "summary"],
        queryFn: () => reportApi.getSummary(contractId),
        staleTime: 0,
      })
      .then((model) => model.commentary);
}

// ─── Mutations ───────────────────────────────────────────────────────────

interface UpdateCommentaryVariables {
  contractId: string;
  input: ReportCommentaryPutInput;
}

/** A stale `version` answers 409; the caller surfaces the conflict rather
 *  than retrying, so another author's narrative is never overwritten
 *  silently. */
export function useUpdateReportCommentary() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contractId, input }: UpdateCommentaryVariables) =>
      reportApi.putCommentary(contractId, input),
    onSuccess: (_data: ReportCommentary, vars) => {
      invalidateReport(queryClient, vars.contractId);
    },
  });
}

interface ServiceCreditVariables {
  contractId: string;
  month: string;
  input: ServiceCreditPutInput;
}

/**
 * Record or replace the credit for one contract-month.
 *
 * Invalidating `["report", contractId]` is the whole refresh: a credit is a
 * report-keyspace row, so the report views and the KPIs tab's credit list
 * both read it through a report query and nothing outside that prefix carries
 * it. A stale `version` answers 409, which the caller surfaces rather than
 * retrying — another author's figure is never overwritten on a guess.
 */
export function useUpsertServiceCredit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contractId, month, input }: ServiceCreditVariables) =>
      reportApi.putServiceCredit(contractId, month, input),
    onSuccess: (_data: ServiceCredit, vars) => {
      invalidateReport(queryClient, vars.contractId);
    },
  });
}

interface DeleteServiceCreditVariables {
  contractId: string;
  month: string;
  input: ServiceCreditDeleteInput;
}

/** Remove a credit recorded in error. The version and the reason are both
 *  required by the server and both reach the audit entry. */
export function useDeleteServiceCredit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contractId, month, input }: DeleteServiceCreditVariables) =>
      reportApi.deleteServiceCredit(contractId, month, input),
    onSuccess: (_data: void, vars) => {
      invalidateReport(queryClient, vars.contractId);
    },
  });
}
